import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowBack, CurrencyRupeeOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import paymentApi from "../../api/paymentApi";
import { searchCustomers } from "../../api/customerApi";
import "./paymentRecord.css";
import backgroundPattern from "../../assets/pasbookpng.png";

const PAYMENT_MODES = ["Cash", "Bank Transfer", "UPI", "Cheque", "Bank Remittance", "Credit Card", "Other"];

export default function RecordPayment() {
  const navigate = useNavigate();
  const location = useLocation();

  const invoiceData = location.state?.invoiceData || null;
  const prefillData = location.state?.prefillData || null;
  const isFromInvoice = Boolean(invoiceData);

  //const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [form, setForm] = useState({
    paymentNumber: "PAY-000001",
    customerName: "",
    customerId: null,
    invoiceId: null,
    invoiceNumber: "",
    amount: "",
    bankCharges: "",
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMode: "Cash",
    referenceNumber: "",
    depositTo: "Cash",
    notes: "",
  });

  // Load next payment number
  useEffect(() => {
    paymentApi.getNextPaymentNumber().then((res) => {
      const num = res?.data?.data || res?.data;
      if (typeof num === "string") setForm((p) => ({ ...p, paymentNumber: num }));
    }).catch(() => {});
  }, []);

  // Pre-fill from invoice
  useEffect(() => {
    if (invoiceData) {
      setForm((p) => ({
        ...p,
        invoiceId: invoiceData.id,
        invoiceNumber: invoiceData.invoiceNumber || "",
        customerName: invoiceData.customerName || invoiceData.customer?.name || "",
        customerId: invoiceData.customerId || invoiceData.customer?.id || null,
      }));

      paymentApi.getInvoicePaymentSummary(invoiceData.id).then((res) => {
        setInvoiceSummary(res?.data?.data || null);
      }).catch(() => {});
    }
  }, [invoiceData]);

  // Pre-fill from prefillData (standalone)
  useEffect(() => {
    if (prefillData && !invoiceData) {
      setForm((p) => ({
        ...p,
        invoiceId: prefillData.invoiceId || null,
        invoiceNumber: prefillData.invoiceNumber || "",
        customerName: prefillData.customerName || "",
        customerId: prefillData.customerId || null,
      }));
    }
  }, [prefillData, invoiceData]);

  // Search customers
  useEffect(() => {
    if (!showCustomerDropdown) return;
    const timer = setTimeout(async () => {
      try {
        const res = await searchCustomers({ name: customerQuery.trim() });
        const apiRows = Array.isArray(res?.data) ? res.data : [];
        setCustomers(apiRows);
      } catch {
        setCustomers([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [customerQuery, showCustomerDropdown]);

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const money = (v) =>
    `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSave = async () => {
    if (!form.customerName.trim()) {
      toast.error("Please select a customer.");
      return;
    }
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Payment amount must be greater than 0.");
      return;
    }

    if (invoiceSummary && amount > Number(invoiceSummary.outstanding)) {
      toast.error("Payment amount cannot exceed the outstanding invoice amount.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        paymentNumber: form.paymentNumber,
        customer: form.customerName,
        customerId: form.customerId,
        invoiceId: form.invoiceId,
        invoiceNumber: form.invoiceNumber,
        amount: amount,
        bankCharges: Number(form.bankCharges) || 0,
        paymentDate: form.paymentDate,
        paymentMode: form.paymentMode,
        referenceNumber: form.referenceNumber,
        depositTo: form.depositTo,
        notes: form.notes,
        status: "Paid",
      };

      await paymentApi.createPayment(payload);
      toast.success("Payment recorded successfully.");
      setTimeout(() => navigate("/dashboard/payments"), 800);
    } catch (error) {
      console.error("Payment save failed:", error);
      toast.error(error?.response?.data?.message || "Failed to save payment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="payment-page">
      <div className="payment-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />

      <header className="payment-header">
        <div className="payment-header-left">
          <div className="payment-page-icon" aria-hidden="true"><CurrencyRupeeOutlined /></div>
          <div className="payment-page-title-wrap">
            <h1>{isFromInvoice ? "Record Payment" : "Record Payment"}</h1>
            <p>{isFromInvoice ? `Payment against invoice ${form.invoiceNumber}` : "Record and manage customer payments."}</p>
          </div>
        </div>
        <button type="button" className="payment-header-back" onClick={() => navigate("/dashboard/payments")} aria-label="Back"><ArrowBack /></button>
      </header>

      <main className="payment-shell">
        <div className="payment-container">

          {/* Invoice Details Section */}
          {isFromInvoice && invoiceSummary && (
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px", marginBottom: 24 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600, color: "#1e293b" }}>Invoice Details</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Invoice Number</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{invoiceSummary.invoiceNumber}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Customer</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{invoiceSummary.customerName}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Invoice Amount</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{money(invoiceSummary.invoiceTotal)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Outstanding</div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: Number(invoiceSummary.outstanding) > 0 ? "#dc2626" : "#16a34a" }}>{money(invoiceSummary.outstanding)}</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Already Paid</div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: "#16a34a" }}>{money(invoiceSummary.totalPaid)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Status</div>
                  <span style={{
                    display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                    background: invoiceSummary.paymentStatus === "PAID" ? "#dcfce7" : invoiceSummary.paymentStatus === "PARTIALLY_PAID" ? "#fef9c3" : "#fee2e2",
                    color: invoiceSummary.paymentStatus === "PAID" ? "#16a34a" : invoiceSummary.paymentStatus === "PARTIALLY_PAID" ? "#a16207" : "#dc2626",
                  }}>{invoiceSummary.paymentStatus}</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-header">
            <h2>Payment Details</h2>
            <button className="close-btn" type="button" onClick={() => navigate("/dashboard/payments")}>&times;</button>
          </div>

          <div className="form-content">
            <div className="payment-section-head">
              <div><h2>Payment Information</h2><p>Enter the payment details below.</p></div>
              <span className="payment-required">* Required</span>
            </div>

            {/* Customer */}
<div className="form-row align-start">
  <label className="required-label">Customer Name*</label>
  <div className="input-group">
    <div className="customer-select-wrapper">
      {isFromInvoice ? (
        <input type="text" value={form.customerName} readOnly style={{ background: "var(--medium-grey)", color: "var(--text-primary)", cursor: "not-allowed", width: "100%", padding: "10px 12px", border: "1px solid var(--border-color)", borderRadius: 8 }} />
      ) : (
        <div style={{ position: "relative", width: "100%" }}>
          <input
            type="text"
            value={form.customerName || customerQuery}
            onChange={(e) => { setCustomerQuery(e.target.value); setShowCustomerDropdown(true); update("customerName", ""); update("customerId", null); }}
            onFocus={() => setShowCustomerDropdown(true)}
            placeholder="Search customer..."
            style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--border-color)", borderRadius: 8, background: "var(--card-bg)", color: "var(--text-primary)" }}
          />
          {showCustomerDropdown && customers.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 8, maxHeight: 200, overflowY: "auto", zIndex: 1000, boxShadow: "var(--shadow-medium)" }}>
              {customers.map((c) => (
                <div key={c.id} 
                style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid var(--border-color)", background: "var(--card-bg)", color: "var(--text-primary)" }} 
                onMouseEnter={(e) => e.currentTarget.style.background = "var(--medium-grey)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "var(--card-bg)"}
                onClick={() => { update("customerName", c.name); update("customerId", c.id); setCustomerQuery(""); setShowCustomerDropdown(false); }}>
                  <strong style={{ color: "var(--text-primary)" }}>{c.name}</strong>
                  <span style={{ fontSize: 12, color: "var(--text-secondary)", marginLeft: 8 }}>{c.contactNumber || c.email}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
</div>

            {/* Invoice Number (read-only) */}
            {form.invoiceNumber && (
              <div className="form-row">
                <label>Invoice Number</label>
                <div className="input-group">
                  <input type="text" value={form.invoiceNumber} readOnly style={{ background: "#f1f5f9", cursor: "not-allowed" }} />
                </div>
              </div>
            )}

            {/* Amount */}
            <div className="form-row">
              <label className="required-label">Amount Received*</label>
              <div className="input-group">
                <div className="currency-input-wrapper">
                  <span className="currency-prefix" aria-hidden="true"><span className="currency-symbol">₹</span></span>
                  <input
                    type="text" inputMode="decimal" autoComplete="off"
                    value={String(form.amount ?? "").replace(/[^0-9.]/g, "")}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1").replace(/^(\d*\.\d{0,2}).*$/, "$1");
                      update("amount", val);
                    }}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Bank Charges */}
            <div className="form-row">
              <label>Bank Charges</label>
              <div className="input-group">
                <input type="number" value={form.bankCharges} onChange={(e) => update("bankCharges", e.target.value)} placeholder="0.00" />
              </div>
            </div>

            {/* Payment Date */}
            <div className="form-row">
              <label className="required-label">Payment Date*</label>
              <div className="input-group">
                <input type="date" value={form.paymentDate} onChange={(e) => update("paymentDate", e.target.value)} />
              </div>
            </div>

            {/* Payment # */}
            <div className="form-row">
              <label className="required-label">Payment #*</label>
              <div className="input-group">
                <input type="text" value={form.paymentNumber} onChange={(e) => update("paymentNumber", e.target.value)} />
              </div>
            </div>

            {/* Payment Mode */}
            <div className="form-row">
              <label>Payment Mode</label>
              <div className="input-group">
                <select value={form.paymentMode} onChange={(e) => update("paymentMode", e.target.value)} style={{ width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            {/* Reference */}
            <div className="form-row">
              <label>Reference / Transaction ID</label>
              <div className="input-group">
                <input type="text" value={form.referenceNumber} onChange={(e) => update("referenceNumber", e.target.value)} placeholder="UTR / Transaction ID / Cheque No" />
              </div>
            </div>

            {/* Notes */}
            <div className="form-block">
              <label>Notes</label>
              <textarea rows="3" value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Internal notes (optional)" />
            </div>

            {/* Summary */}
            <div className="summary-wrapper">
              <div className="summary-card">
                <div className="summary-line">
                  <span>Amount Received:</span>
                  <span>{money(form.amount)}</span>
                </div>
                {invoiceSummary && (
                  <>
                    <div className="summary-line">
                      <span>Outstanding Before Payment:</span>
                      <span>{money(invoiceSummary.outstanding)}</span>
                    </div>
                    <div className="summary-line excess-line">
                      <span>Outstanding After Payment:</span>
                      <span className="excess-val">{money(Math.max(0, Number(invoiceSummary.outstanding) - Number(form.amount || 0)))}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate("/dashboard/payments")}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Payment"}
            </button>
          </div>

        </div>
      </main>
    </div>
  );
}
